import React, { useEffect, useRef } from "react";
import { useThree } from "@react-three/fiber";
import { useGesture } from "@use-gesture/react";
import * as THREE from "three";
import { useShelvingStore, useStoreSystem } from "../../../store";
import shallow from "zustand/shallow";
import { SectionItemI } from "../../../shared";
import { SideType } from "../../../store/types/store";
import { calcItemPosition, getSectionId } from "../../../store/actions/items/calcItemPosition";
import { changeAllChildrenMaterial } from "../../../store/actions/items/changeAllChildrenColor";
import { checkPositionErrors } from "../../../store/actions/items/checkPositionErrors";
import {useColors} from "../../../hooks/useColors";
import {useLocation} from "react-router-dom";

interface IDraggable {
    onDragStart: any;
    onDragEnd: any;
    onDrag: any;
    onClick?: any;
    onEdit: any;
    onPointerMissed?: any;
    side: SideType;
    shelf: SectionItemI;
    itemId: string;
    sectionId: string;
    position: number[];
    leftRacksStart: number[];
    name?: string;
    shift: number[];
    startXPosition: number;
}

const changedColors: Record<string, boolean> = {};
const changedRodColors: Record<string, boolean> = {};
const refs: { room: THREE.Group | null } = { room: null };

export const Draggable = ({
    children,
    onDragStart,
    onDragEnd,
    onDrag,
    onClick,
    onEdit,
    position,
    itemId,
    onPointerMissed,
    sectionId,
    leftRacksStart,
    shelf,
    side,
    shift,
    startXPosition,
    ...props
}: React.PropsWithChildren<IDraggable>) => {
    const moveItem = useShelvingStore((state) => state.moveItem, shallow);
    const removeSpiritItems = useShelvingStore((state) => state.removeSpiritItems, shallow);
    const refCurrentItem = useRef<{
        currentGroup: THREE.Group;
        shelf: THREE.Group;
        cursorPosition?: [number, number, number];
    } | null>(null);
    const { colorsShade, colorsItems } = useColors();
    const currentItemId = useStoreSystem.getState().currentEditItem.id;
    const { gl } = useThree();

    const isHoldButton = useStoreSystem((state) => state.sectionFilling.isHoldButton);
    const currentSide = useStoreSystem((state) => state.currentSide);
    const location = useStoreSystem((state) => state.location);
    const setSystemProperties = useStoreSystem((state) => state.setSystemProperties, shallow);

    const { raycaster, size, camera, scene } = useThree();
    const { mouse2D } = {
        mouse2D: new THREE.Vector2(),
    };
    let movedDistance: any[] = [];

    useEffect(() => {
        if (currentItemId) {
            if (!refs.room) {
                // сохраним ссылку на комнату, чтобы постоянно не перебирать сцену
                refs.room = scene.getObjectByName("room") as THREE.Group;
            }

            // менять положение будем непосредственно у полки
            const currentGroup = scene.getObjectByName(currentItemId) as THREE.Group;
            if (currentGroup) {
                const shelf = currentGroup.getObjectByName("shelf") as THREE.Group;
                if (shelf) {
                    refCurrentItem.current = { currentGroup, shelf };
                }
            }
        }
    }, [currentItemId]);

    const bind = useGesture({
        onDrag: (state: any) => {
            if (currentSide !== side || location?.includes("view")) {
                return;
            }
            // Запрещаем перетаскивание родов - они должны двигаться только с полками
            // Но разрешаем для призрачных родов (isSpirit)
            if (shelf.type?.includes("Штанга") && !shelf.isSpirit) {
                return;
            }
            const {
                xy: [x, y],
                delta,
                distance,
                event,
            } = state;

            movedDistance = distance;
            if (delta[0] === 0 && delta[1] === 0) return;
            // Получаем позицию канваса относительно окна
            const canvasElement = gl.domElement;
            const rect = canvasElement?.getBoundingClientRect() || { left: 0, top: 0 };

            // Корректируем координаты мыши с учетом позиции канваса
            const canvasX = x - rect.left;
            const canvasY = y - rect.top;

            const nx = (canvasX / size.width) * 2 - 1;
            const ny = (-canvasY / size.height) * 2 + 1;
            mouse2D.set(nx, ny);

            let currSectionId,
                newSectionId = "";

            if (refCurrentItem.current && currentItemId && refs.room) {
                raycaster.setFromCamera(mouse2D, camera);
                const intersect = raycaster.intersectObject(refs.room)?.[0]?.point;
                if (!intersect) {
                    return;
                }
                const local = refCurrentItem.current.currentGroup.worldToLocal(intersect.clone());
                const newX = local.x;
                const newY = intersect.y;

                // временно прикрепляем консоли к полке. при перендере это сбросится само
                if (refCurrentItem.current.shelf.children.length === 1) {
                    const children: THREE.Object3D[] = refCurrentItem.current.currentGroup.children.filter(
                        (child) => child.name !== "shelf"
                    );
                    children.forEach((child) => refCurrentItem.current?.shelf?.attach(child));
                }
                const newPosition = calcItemPosition(currentItemId, [newX, newY, position[2]]);

                if (newPosition) {
                    refCurrentItem.current.cursorPosition = [newX, newY, position[2]];
                    const posX = startXPosition + newPosition[0];
                    const posY = newPosition[1] - shift[1];

                    // перемещаем непосредственно модель в сцене, стейт обновляем только по окончании перемещения
                    refCurrentItem.current.shelf.position.x = posX;
                    refCurrentItem.current.shelf.position.y = posY;

                    // Если перемещается полка, также перемещаем связанные с ней роды
                    if (shelf.type?.includes("Полка")) {
                        const currentShelving = useShelvingStore.getState().getCurrentShelving();

                        // Находим все роды на той же высоте и в той же позиции по X (используем исходную позицию полки)
                        currentShelving.sections.forEach((section) => {
                            section.items.forEach((item) => {
                                if (
                                    item.type.some((t) => t.includes("Штанга")) &&
                                    Math.abs(item.position[1] - position[1]) < 0.01 &&
                                    item.sectionID === sectionId &&
                                    item.id !== currentItemId
                                ) {
                                    // Находим 3D объект рода в сцене и перемещаем его
                                    const rodGroup = scene.getObjectByName(item.id) as THREE.Group;
                                    if (rodGroup) {
                                        const rodShelf = rodGroup.getObjectByName("shelf") as THREE.Group;
                                        if (rodShelf) {
                                            rodShelf.position.x = posX;
                                            rodShelf.position.y = posY;

                                            // Делаем род зеленым призраком, как и полку
                                            if (!changedRodColors[item.id]) {
                                                changeAllChildrenMaterial(
                                                    rodShelf,
                                                    0x008d24,
                                                    {
                                                        transparent: true,
                                                        opacity: 0.2,
                                                        roughness: 0.2,
                                                        metalness: 0,
                                                    },
                                                    item.id
                                                );
                                                changedRodColors[item.id] = true;
                                            }
                                        }
                                    }
                                }
                            });
                        });
                    }

                    if (!shelf.type?.includes("Подвес")) {
                        const hasIntersections = checkPositionErrors(currentItemId, [posX, posY, position[2]]);

                        // факт перекрашивания сохраняем во внешней переменной, что бы не красить на каждое перемещение
                        if (hasIntersections && !changedColors[refCurrentItem.current.shelf.uuid]) {
                            changeAllChildrenMaterial(refCurrentItem.current.shelf, 0xeb3d00, {}, itemId);
                            changedColors[refCurrentItem.current.shelf.uuid] = true;
                        }
                        if (!hasIntersections && changedColors[refCurrentItem.current.shelf.uuid]) {
                            changeAllChildrenMaterial(refCurrentItem.current.shelf, 0x008d24, {}, itemId);
                            changedColors[refCurrentItem.current.shelf.uuid] = false;
                        }
                        [currSectionId, newSectionId] = getSectionId(currentItemId, [newX, newY, position[2]]);
                    }
                }
            }
            onDrag?.(currSectionId, newSectionId);
        },
        onDragStart: ({ event }) => {
            if (currentSide !== side || location?.includes("view")) {
                return;
            }
            // Для родов запрещаем перетаскивание и зеленую подсветку при dragStart
            // Но разрешаем полное взаимодействие для призрачных родов (isSpirit)
            if (shelf.type?.includes("Штанга") && !shelf.isSpirit) {
                // Вызываем onEdit сразу для родов (без onDragStart чтобы не было зеленой подсветки)
                onEdit?.();
                event.stopPropagation();
                return;
            }
            if (isHoldButton && !shelf.isSpirit) {
                removeSpiritItems();
                setSystemProperties([{ name: "sectionFilling", subName: "isHoldButton", value: false }]);
            }

            // Очищаем состояние изменённых цветов родов перед началом перетаскивания
            Object.keys(changedRodColors).forEach((key) => {
                delete changedRodColors[key];
            });

            onDragStart?.(event);
            event.stopPropagation();
        },
        onDragEnd: ({ event }) => {
            if (currentSide !== side || location?.includes("view")) {
                return;
            }
            // Для родов запрещаем перетаскивание
            // Полное взаимодействие разрешено для призрачных родов (isSpirit)
            if (shelf.type?.includes("Штанга") && !shelf.isSpirit) {
                // Для родов ничего не делаем в onDragEnd, выделение уже произошло в onDragStart
                return;
            }
            if (currentItemId && refCurrentItem.current?.cursorPosition) {
                moveItem(currentItemId, [...refCurrentItem.current.cursorPosition]);
                delete changedColors[refCurrentItem.current.shelf.uuid];
            }
            if (movedDistance[0] === 0 && movedDistance[1] === 0 && !shelf.isSpirit) {
                onEdit?.();
                return;
            }
            if (refCurrentItem?.current?.shelf && !shelf.type?.includes("Подвес")) {
                changeAllChildrenMaterial(refCurrentItem.current.shelf, colorsShade[colorsItems.brackets], {}, itemId);
            }

            // Восстанавливаем нормальный цвет родов после окончания перетаскивания полки
            if (shelf.type?.includes("Полка")) {
                const currentShelving = useShelvingStore.getState().getCurrentShelving();

                currentShelving.sections.forEach((section) => {
                    section.items.forEach((item) => {
                        if (
                            item.type.some((t) => t.includes("Штанга")) &&
                            Math.abs(item.position[1] - position[1]) < 0.01 &&
                            Math.abs(item.position[0] - position[0]) < 0.01 &&
                            item.sectionID === sectionId &&
                            item.id !== currentItemId
                        ) {
                            const rodGroup = scene.getObjectByName(item.id) as THREE.Group;
                            if (rodGroup) {
                                const rodShelf = rodGroup.getObjectByName("shelf") as THREE.Group;
                                if (rodShelf) {
                                    // Восстанавливаем нормальный цвет рода используя правильную логику цветов
                                    rodShelf.traverse((object: THREE.Object3D) => {
                                        // Для родов используем цвет кронштейнов
                                        const changeObjMaterial = (
                                            obj: THREE.Object3D,
                                            mainColor: THREE.Color,
                                            secondColor = mainColor
                                        ) => {
                                            const changeColors = (
                                                materialName: string,
                                                material: any,
                                                color: THREE.Color
                                            ) => {
                                                if (materialName) {
                                                    material.transparent = false;
                                                    material.color = color;
                                                    material.opacity = 1;
                                                    material.roughness = 0.8;
                                                    material.metalness = 0.5;
                                                }
                                            };
                                            if (obj.children.length > 0) {
                                                obj.children.forEach((mesh: any, index: number) => {
                                                    if (mesh instanceof THREE.Mesh) {
                                                        const material = (obj.children[index] as THREE.Mesh)
                                                            .material as THREE.Material;
                                                        const materialName = material?.name;
                                                        const color =
                                                            obj.children.length > 1 && index === obj.children.length - 1
                                                                ? secondColor
                                                                : mainColor;
                                                        changeColors(materialName, material, color);
                                                    } else if (mesh instanceof THREE.Object3D) {
                                                        mesh.traverse((innerObj: THREE.Object3D) => {
                                                            const material = (innerObj as any).material;
                                                            const materialName = material?.name;
                                                            changeColors(materialName, material, mainColor);
                                                        });
                                                    }
                                                });
                                            }
                                        };
                                        changeObjMaterial(object, colorsShade[colorsItems.brackets]);
                                    });
                                    delete changedRodColors[item.id];
                                }
                            }
                        }
                    });
                });
            }
            onDragEnd?.(event);
            event.stopPropagation();
        },
    });

    return (
        // @ts-ignore
        <group {...bind()} {...props}>
            {children}
        </group>
    );
};
